/**
 * String utilities
 * Pure functions for string manipulation
 */

/**
 * Capitalizes the first letter of a string
 * @param str - String to capitalize
 * @returns String with first letter capitalized
 * @example capitalizeFirstLetter("hello") => "Hello"
 */
export function capitalizeFirstLetter(str?: string): string {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Strips HTML tags from a string while preserving spaces
 * Converts <br>, <p>, <div> etc. to spaces to maintain readability
 * @param html - HTML string to strip
 * @returns Plain text without HTML tags
 * @example
 * stripHtmlTags("<p>Hello</p><p>World</p>") => "Hello World"
 * stripHtmlTags("<div>Hello<br/>World</div>") => "Hello World"
 */
export function stripHtmlTags(html?: string): string {
    if (!html) return "";

    return (
        html
            // Replace block elements with spaces
            .replace(/<\/(p|div|h[1-6]|li|tr|td|th)>/gi, " ")
            .replace(/<(br|hr)\s*\/?>/gi, " ")
            // Remove all remaining HTML tags
            .replace(/<[^>]+>/g, "")
            // Decode common HTML entities
            .replace(/&nbsp;/g, " ")
            .replace(/&amp;/g, "&")
            .replace(/&lt;/g, "<")
            .replace(/&gt;/g, ">")
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'")
            // Replace multiple spaces with single space
            .replace(/\s+/g, " ")
            // Trim whitespace from start and end
            .trim()
    );
}

export function stripLeadingAbout(str?: string): string {
    if (!str) return "";
    return str.replace(/^\s*about\s+/i, "").trim();
}

/**
 * Decodes HTML entities in a string without removing tags.
 * Useful when an API returns double-encoded HTML (e.g. &lt;p&gt; instead of <p>).
 * @param html - String with HTML entities
 * @returns String with decoded entities
 * @example decodeHtmlEntities("&lt;p&gt;Hello&lt;/p&gt;") => "<p>Hello</p>"
 */
export function decodeHtmlEntities(html?: string): string {
    if (!html) return "";

    return html
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&amp;/g, "&");
}

/**
 * Truncates a string to a specified length and adds ellipsis
 * @param str - String to truncate
 * @param maxLength - Maximum length (default: 100)
 * @param ellipsis - String to append (default: "...")
 * @returns Truncated string with ellipsis
 * @example
 * truncateText("This is a very long text", 10) => "This is a..."
 * truncateText("Short", 10) => "Short"
 */
export function truncateText(str?: string, maxLength: number = 100, ellipsis: string = "..."): string {
    if (!str) return "";
    if (str.length <= maxLength) return str;

    const truncated = str.slice(0, maxLength);

    const lastSpace = truncated.lastIndexOf(" ");

    if (lastSpace > 0) {
        return truncated.slice(0, lastSpace) + ellipsis;
    }

    return truncated + ellipsis;
}
